'use client';

import { useState } from 'react';
import { useAppStore } from '@/lib/stores/app-store';
import { API } from '@/lib/utils/api';
import { MapPanel } from '@/components/map/MapPanel';
import { STTInput } from '@/components/audio/STTInput';
import { ArrowLeft, ChevronRight, Play, RefreshCw } from 'lucide-react';
import { motion } from 'framer-motion';
import type { MapScope } from '@/lib/types';

export function SelectScreen() {
  const {
    language,
    topicInput,
    selectedScope,
    subjectMap,
    selectedNodeId,
    setTopicInput,
    setSelectedScope,
    setSubjectMap,
    setTrail,
    setSelectedNodeId,
    setScreen,
    setLoading,
    isLoading,
    saveCurrentSession,
  } = useAppStore();

  const [step, setStep] = useState<'topic' | 'scope' | 'ready'>('topic');
  const isHebrew = language === 'he';

  const text = {
    en: {
      back: 'Back',
      whatToLearn: 'What do you want to learn?',
      placeholder: 'e.g., limits, calculus, addition...',
      next: 'Next',
      selectScope: 'Select map scope',
      scopeNarrow: 'Narrow',
      scopeNarrowDesc: 'Just this topic',
      scopeMedium: 'Medium',
      scopeMediumDesc: 'Course level',
      scopeBroad: 'Broad',
      scopeBroadDesc: 'Full discipline',
      generateMap: 'Generate Map',
      regenerateMap: 'Regenerate Map',
      generating: 'Generating...',
      selectedTopic: 'Selected topic:',
      startLearning: 'Start Learning',
      selectFromMap: 'Select a topic from the map to start',
    },
    he: {
      back: 'חזור',
      whatToLearn: 'מה אתה רוצה ללמוד?',
      placeholder: 'למשל: גבולות, חשבון דיפרנציאלי, חיבור...',
      next: 'הבא',
      selectScope: 'בחר טווח מפה',
      scopeNarrow: 'צר',
      scopeNarrowDesc: 'רק נושא זה',
      scopeMedium: 'בינוני',
      scopeMediumDesc: 'ברמת קורס',
      scopeBroad: 'רחב',
      scopeBroadDesc: 'תחום מלא',
      generateMap: 'צור מפה',
      regenerateMap: 'צור מפה מחדש',
      generating: 'יוצר...',
      selectedTopic: 'נושא נבחר:',
      startLearning: 'התחל ללמוד',
      selectFromMap: 'בחר נושא מהמפה כדי להתחיל',
    },
  };

  const t = text[language];

  const scopes: { value: MapScope; label: string; desc: string }[] = [
    { value: 'narrow', label: t.scopeNarrow, desc: t.scopeNarrowDesc },
    { value: 'medium', label: t.scopeMedium, desc: t.scopeMediumDesc },
    { value: 'broad', label: t.scopeBroad, desc: t.scopeBroadDesc },
  ];

  const handleGenerateMap = async () => {
    setLoading(true);
    setSelectedNodeId(null); // Clear previous selection
    try {
      const response = await fetch(API.generateMap, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          topic: topicInput,
          scope: selectedScope,
          language,
        }),
      });

      if (!response.ok) throw new Error('Failed to generate map');

      const data = await response.json();
      setSubjectMap(data.map);
      setTrail(data.trail);
      setStep('ready');
    } catch (error) {
      console.error('Error generating map:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartLearning = () => {
    if (selectedNodeId) {
      // Save session before starting
      saveCurrentSession(topicInput);
      setScreen('learn');
    }
  };

  const selectedNode = subjectMap?.nodes[selectedNodeId || ''];

  return (
    <div
      className="min-h-screen flex"
      dir={isHebrew ? 'rtl' : 'ltr'}
    >
      {/* Left Panel - Inputs */}
      <div className="w-1/2 border-r border-border p-8 flex flex-col">
        {/* Back button */}
        <button
          onClick={() => setScreen('welcome')}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 w-fit"
        >
          <ArrowLeft className="w-4 h-4" />
          {t.back}
        </button>

        <div className="flex-1 flex flex-col justify-center max-w-md mx-auto w-full">
          {/* Step 1: Topic Input */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <label className="block text-lg font-medium mb-3">
              {t.whatToLearn}
            </label>
            <STTInput
              value={topicInput}
              onChange={setTopicInput}
              onSubmit={() => topicInput.trim() && setStep('scope')}
              placeholder={t.placeholder}
              disabled={isLoading}
              language={language}
            />
            {step === 'topic' && (
              <button
                onClick={() => topicInput.trim() && setStep('scope')}
                disabled={!topicInput.trim()}
                className="btn btn-primary mt-4 w-full"
              >
                {t.next}
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </motion.div>

          {/* Step 2: Scope Selection */}
          {(step === 'scope' || step === 'ready') && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
            >
              <label className="block text-lg font-medium mb-3">
                {t.selectScope}
              </label>
              <div className="space-y-2">
                {scopes.map((scope) => (
                  <button
                    key={scope.value}
                    onClick={() => setSelectedScope(scope.value)}
                    className={`w-full p-4 rounded-lg border text-left transition-colors ${
                      selectedScope === scope.value
                        ? 'border-primary bg-primary/10'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <div className="font-medium">{scope.label}</div>
                    <div className="text-sm text-muted-foreground">
                      {scope.desc}
                    </div>
                  </button>
                ))}
              </div>
              <button
                onClick={handleGenerateMap}
                disabled={isLoading}
                className={`btn mt-4 w-full ${step === 'ready' ? 'btn-secondary' : 'btn-primary'}`}
              >
                {isLoading ? (
                  <span className="animate-pulse">{t.generating}</span>
                ) : (
                  <>
                    {step === 'ready' ? <RefreshCw className="w-4 h-4" /> : null}
                    {step === 'ready' ? t.regenerateMap : t.generateMap}
                    {step !== 'ready' ? <ChevronRight className="w-4 h-4" /> : null}
                  </>
                )}
              </button>
            </motion.div>
          )}

          {/* Step 3: Selected Topic & Start */}
          {step === 'ready' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="border-t border-border pt-8"
            >
              <label className="block text-sm text-muted-foreground mb-2">
                {t.selectedTopic}
              </label>
              <div className="p-4 rounded-lg bg-card border border-border mb-4">
                {selectedNode ? (
                  <span className="font-medium">
                    {isHebrew ? selectedNode.titleHe || selectedNode.title : selectedNode.title}
                  </span>
                ) : (
                  <span className="text-muted-foreground italic">
                    {t.selectFromMap}
                  </span>
                )}
              </div>
              <button
                onClick={handleStartLearning}
                disabled={!selectedNodeId}
                className="btn btn-primary w-full text-lg py-4"
              >
                <Play className="w-5 h-5" />
                {t.startLearning}
              </button>
            </motion.div>
          )}
        </div>
      </div>

      {/* Right Panel - Map/Trail/Stats Tabs */}
      <div className="w-1/2 bg-muted/30">
        <MapPanel
          showTabs={step === 'ready'}
          onSelectNode={setSelectedNodeId}
          selectedNodeId={selectedNodeId}
        />
      </div>
    </div>
  );
}
